/**
 * Compact date ranges: "Sep 7 – 13, 2026" rather than
 * "Sep 7, 2026 - Sep 13, 2026" (#277).
 *
 * Why a hand-written moment table rather than Intl: Hermes, the engine the
 * mobile app ships, has no Intl range formatter, and react-intl's date-time
 * range helper depends on it. Jest runs on Node, whose ICU does
 * implement it, so code using it would pass every test and throw on a phone.
 * `__tests__/utils/Dates/noFormatRange.test.ts` keeps it out of core and
 * mobile.
 *
 * The token order of each entry was drafted from Node's ICU (CLDR) range
 * patterns and then hand-reviewed against the app. Where CLDR picks a numeric
 * style (ja, cs, sk, bg), the entry keeps the month-name style the locale's
 * `ll` already renders elsewhere in the app. Separator spacing follows CLDR:
 * a bare en dash inside a month for most locales, thin spaces around it for
 * most Latin-script locales, and ordinary spaces where CLDR uses them.
 *
 * This module imports moment only, never the utils barrel
 * (see `importCycles.test.ts`).
 */
import moment from 'moment-timezone';

export type RangeStyle = 'visual' | 'spoken';

export type RangeFormatOptions = {
  /**
   * `visual` (default) abbreviates months for the screen; `spoken` spells
   * them out for a screen-reader label.
   */
  style?: RangeStyle;
  /**
   * Joins the two ends in the spoken style, e.g. with a translated
   * "{start} to {end}". Supplied by the caller so core stays free of intl.
   * Without it, the spoken ends are joined with the visual separator.
   */
  joinSpokenRange?: (start: string, end: string) => string;
};

/** `[start format, end format, separator]`, in moment tokens. */
export type RangeCase = readonly [
  start: string,
  end: string,
  separator: string,
];

export type RangeCases = {
  /** Both ends in the same month: "Sep 7 – 13, 2026". */
  sameMonth: RangeCase;
  /** Same year, two months: "Aug 31 – Sep 6, 2026". */
  sameYear: RangeCase;
  /** Two years: "Dec 28, 2026 – Jan 3, 2027". */
  crossYear: RangeCase;
};

export type LocaleRangeFormats = RangeCases & {
  /**
   * Spoken formats, when the mechanical derivation (`MMM` → `MMMM`,
   * `ll` → `LL`) would not read like the locale's own `LL`.
   */
  spoken?: RangeCases;
};

const EN_DASH = '\u2013';
const THIN_SPACE = '\u2009';

/** "7–13" */
const TIGHT = EN_DASH;
/** "Aug 31 – Sep 6", with U+2009 THIN SPACE on both sides. */
const THIN = `${THIN_SPACE}${EN_DASH}${THIN_SPACE}`;
/** "31 авг. – 6 сент.", with ordinary spaces. */
const SPACED = ` ${EN_DASH} `;
/** U+FF5E, CLDR's ja range separator. */
const FULLWIDTH_TILDE = '\uff5e';

const llRange = (separator: string): RangeCase => ['ll', 'll', separator];

/**
 * Keyed by moment locale name: the app's `sr` is moment's `sr-cyrl`
 * (`Dates.setLocale`). `RANGE_FORMATS` must cover every app locale; a test
 * compares it with `packages/i18n/src/translations`.
 */
export const RANGE_FORMATS: Record<string, LocaleRangeFormats> = {
  ar: {
    sameMonth: ['D', 'D MMM YYYY', TIGHT],
    sameYear: ['D MMM', 'D MMM YYYY', SPACED],
    crossYear: llRange(SPACED),
  },
  bg: {
    sameMonth: ['D', 'D MMM YYYY', SPACED],
    sameYear: ['D MMM', 'D MMM YYYY', SPACED],
    crossYear: llRange(SPACED),
  },
  ca: {
    sameMonth: ['D', 'D MMM YYYY', TIGHT],
    sameYear: ['D MMM', 'D MMM YYYY', THIN],
    crossYear: llRange(THIN),
    // ca's LL is "D MMMM [de] YYYY": the long month already carries its own
    // "de"/"d'", but the year needs one too.
    spoken: {
      sameMonth: ['D', 'D MMMM [de] YYYY', TIGHT],
      sameYear: ['D MMMM', 'D MMMM [de] YYYY', THIN],
      crossYear: ['LL', 'LL', THIN],
    },
  },
  cs: {
    sameMonth: ['D.', 'D. MMM YYYY', TIGHT],
    sameYear: ['D. MMM', 'D. MMM YYYY', THIN],
    crossYear: llRange(THIN),
  },
  da: {
    sameMonth: ['D.', 'D. MMM YYYY', TIGHT],
    sameYear: ['D. MMM', 'D. MMM YYYY', TIGHT],
    crossYear: llRange(TIGHT),
  },
  de: {
    sameMonth: ['D.', 'D. MMM YYYY', TIGHT],
    sameYear: ['D. MMM', 'D. MMM YYYY', THIN],
    crossYear: llRange(THIN),
  },
  el: {
    sameMonth: ['D', 'D MMM YYYY', TIGHT],
    sameYear: ['D MMM', 'D MMM YYYY', SPACED],
    crossYear: llRange(SPACED),
  },
  en: {
    sameMonth: ['MMM D', 'D, YYYY', THIN],
    sameYear: ['MMM D', 'MMM D, YYYY', THIN],
    crossYear: llRange(THIN),
  },
  es: {
    sameMonth: ['D', 'D [de] MMM [de] YYYY', TIGHT],
    sameYear: ['D [de] MMM', 'D [de] MMM [de] YYYY', THIN],
    crossYear: llRange(THIN),
  },
  fr: {
    sameMonth: ['D', 'D MMM YYYY', TIGHT],
    sameYear: ['D MMM', 'D MMM YYYY', THIN],
    crossYear: llRange(THIN),
  },
  hr: {
    sameMonth: ['Do', 'Do MMM YYYY', THIN],
    sameYear: ['Do MMM', 'Do MMM YYYY', THIN],
    crossYear: llRange(THIN),
  },
  hu: {
    sameMonth: ['YYYY. MMM D', 'D.', TIGHT],
    sameYear: ['YYYY. MMM D.', 'MMM D.', THIN],
    crossYear: llRange(THIN),
  },
  it: {
    sameMonth: ['D', 'D MMM YYYY', TIGHT],
    sameYear: ['D MMM', 'D MMM YYYY', THIN],
    crossYear: llRange(THIN),
  },
  ja: {
    // ja months have no short/long split, so the spoken style is the same.
    sameMonth: ['YYYY[年]M[月]D[日]', 'D[日]', FULLWIDTH_TILDE],
    sameYear: ['YYYY[年]M[月]D[日]', 'M[月]D[日]', FULLWIDTH_TILDE],
    crossYear: llRange(FULLWIDTH_TILDE),
  },
  ko: {
    sameMonth: ['YYYY[년] MMMM D[일]', 'D[일]', '~'],
    sameYear: ['YYYY[년] MMMM D[일]', 'MMMM D[일]', ' ~ '],
    crossYear: llRange(' ~ '),
  },
  nb: {
    sameMonth: ['D.', 'D. MMM YYYY', TIGHT],
    sameYear: ['D. MMM', 'D. MMM YYYY', TIGHT],
    crossYear: llRange(TIGHT),
  },
  nl: {
    sameMonth: ['D', 'D MMM YYYY', TIGHT],
    sameYear: ['D MMM', 'D MMM YYYY', THIN],
    crossYear: llRange(THIN),
  },
  pl: {
    sameMonth: ['D', 'D MMM YYYY', TIGHT],
    sameYear: ['D MMM', 'D MMM YYYY', TIGHT],
    crossYear: llRange(TIGHT),
  },
  pt: {
    sameMonth: ['D', 'D [de] MMM [de] YYYY', THIN],
    sameYear: ['D [de] MMM', 'D [de] MMM [de] YYYY', THIN],
    crossYear: llRange(THIN),
  },
  ru: {
    sameMonth: ['D', 'D MMM YYYY [г.]', TIGHT],
    sameYear: ['D MMM', 'D MMM YYYY [г.]', SPACED],
    crossYear: llRange(SPACED),
  },
  sk: {
    sameMonth: ['D.', 'D. MMM YYYY', THIN],
    sameYear: ['D. MMM', 'D. MMM YYYY', THIN],
    crossYear: llRange(THIN),
  },
  'sr-cyrl': {
    sameMonth: ['D.', 'D. MMM YYYY.', TIGHT],
    sameYear: ['D. MMM', 'D. MMM YYYY.', SPACED],
    crossYear: llRange(SPACED),
  },
  sv: {
    sameMonth: ['D', 'D MMM YYYY', TIGHT],
    sameYear: ['D MMM', 'D MMM YYYY', TIGHT],
    crossYear: llRange(TIGHT),
  },
  tr: {
    sameMonth: ['D', 'D MMM YYYY', TIGHT],
    sameYear: ['D MMM', 'D MMM YYYY', THIN],
    crossYear: llRange(THIN),
  },
  uk: {
    sameMonth: ['D', 'D MMM YYYY [р.]', TIGHT],
    sameYear: ['D MMM', 'D MMM YYYY [р.]', SPACED],
    crossYear: llRange(SPACED),
  },
};

/** For a moment locale with no entry: "ll – ll". */
const FALLBACK: LocaleRangeFormats = {
  sameMonth: llRange(SPACED),
  sameYear: llRange(SPACED),
  crossYear: llRange(SPACED),
};

/** `MMM` → `MMMM` and `ll` → `LL`, leaving `[literal]` text alone. */
const toSpokenFormat = (format: string) =>
  format.replace(/\[[^\]]*\]|MMMM|MMM|ll/g, (token) => {
    if (token === 'MMM') {
      return 'MMMM';
    }
    if (token === 'll') {
      return 'LL';
    }
    return token;
  });

const toSpokenCase = ([start, end, separator]: RangeCase): RangeCase => [
  toSpokenFormat(start),
  toSpokenFormat(end),
  separator,
];

const spokenCases = (formats: LocaleRangeFormats): RangeCases =>
  formats.spoken ?? {
    sameMonth: toSpokenCase(formats.sameMonth),
    sameYear: toSpokenCase(formats.sameYear),
    crossYear: toSpokenCase(formats.crossYear),
  };

/**
 * Formats `[start, end]` (unix seconds) as a compact range in `timezone`, in
 * the active moment locale.
 *
 * Built for calendar periods that span at least two days, with
 * `start < end` (a week today). A single-day range would print "Sep 7 – 7";
 * callers with such ranges should format a single date instead.
 */
export const formatDateRange = (
  start: number,
  end: number,
  timezone: string,
  {style = 'visual', joinSpokenRange}: RangeFormatOptions = {},
) => {
  const from = moment.tz(start * 1000, timezone);
  const to = moment.tz(end * 1000, timezone);
  const formats = RANGE_FORMATS[from.locale()] ?? FALLBACK;
  const cases = style === 'spoken' ? spokenCases(formats) : formats;

  let rangeCase = cases.sameMonth;
  if (from.year() !== to.year()) {
    rangeCase = cases.crossYear;
  } else if (from.month() !== to.month()) {
    rangeCase = cases.sameYear;
  }
  const [startFormat, endFormat, separator] = rangeCase;
  const startText = from.format(startFormat);
  const endText = to.format(endFormat);

  if (style === 'spoken' && joinSpokenRange) {
    return joinSpokenRange(startText, endText);
  }
  return `${startText}${separator}${endText}`;
};
